import { Box, Button, Switch, styled, Tooltip } from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import ButtonGroup from 'components/ButtonGroup';
import { FirmSelector } from 'components/FirmSelector';
import AppDatePicker from 'components/FormElements/AppDatePicker';
import AppRadioButton from 'components/FormElements/AppRadioButton';
import TextField from 'components/FormElements/AppTextInput';
import FieldRow from 'components/FormElements/FieldRow';
import RecordView from 'components/RecordView';
import { FundStatus, TrancheVisibility } from 'further-types/fund';
import { CalendarDay } from 'further-ui/utils';
import { useFirm } from 'hooks/data/firm/useFirm';
import { Control, Controller, useWatch } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { makeStyles } from 'tss-react/mui';
import { EisTag } from '../types';
import type { TrancheFormInput } from '../useTrancheForm';

const ToggleContainer = styled(Box)(() => ({
  width: 'fit-content',
}));

const useStyles = makeStyles()((theme) => ({
  marginLeftBtn: {
    marginLeft: 10,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  switchLabel: {
    margin: '0 10px',

    '&:first-of-type': {
      marginLeft: 0,
    },
  },
  tooltipPadding: {
    paddingLeft: 20,
  },
  tooltipLink: {
    color: theme.palette.text.rootColor,
  },
}));

type Props = {
  control: Control<TrancheFormInput>;
  handleCancel: () => void;
  handleClick: () => void;
  id: string;
  investment: Array<{ id: string }>;
  tagList: Array<EisTag>;
  buttonDisabled?: boolean;
  hasPrerequisitesForPublishing: boolean;
  usesDealDocumentation: boolean;
  viewOnly?: boolean;
};

const BasicInformation: React.FC<Props> = ({
  control,
  handleCancel,
  handleClick,
  id,
  investment,
  tagList,
  buttonDisabled = false,
  hasPrerequisitesForPublishing,
  usesDealDocumentation,
  viewOnly = false,
}) => {
  const { classes } = useStyles();

  const firmId = useWatch({ control, name: 'firmId' });
  const fundStatus = useWatch({ control, name: 'fundStatus' });

  const { firms } = useFirm({ params: { firmId } });
  const permittedClassifications =
    firms?.data?.result?.[0]?.whiteLabelConfig?.permittedClassifications;
  const allowProfessionalClients = permittedClassifications?.professional;

  const isDraft = fundStatus === FundStatus.Draft;
  const isOpen = fundStatus === FundStatus.Open;

  return (
    <RecordView>
      <FieldRow title="Firm*" centerTitle>
        <Controller
          name="firmId"
          control={control}
          render={({ field, fieldState }) => (
            <FirmSelector
              firmId={field.value}
              onChange={field.onChange}
              placeholder="Select firm"
              allowDeselection={false}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              required
              disabled={viewOnly}
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Tranche name*" centerTitle>
        <Controller
          name="fundName"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              required
              name="fundName"
              disabled={viewOnly}
              fullWidth
              onChange={(event) => field.onChange(event.target.value)}
              value={field.value}
              placeholder="Enter the tranche name"
              variant="outlined"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </FieldRow>
      <FieldRow
        title="Tranche status*"
        centerTitle
        tooltipText={
          !hasPrerequisitesForPublishing && !isOpen
            ? `To publish this tranche, please first select your payment account in "Bank details"${
                !usesDealDocumentation
                  ? ' and add your Legal agreement in "Tranche subscription documentation".'
                  : '.'
              }`
            : ''
        }
      >
        <Controller
          name="fundStatus"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <AppRadioButton
                required
                key="open"
                name="fundStatus"
                label="Open"
                value={1}
                checked={field.value === 1}
                onChange={() => field.onChange(1)}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                disabled={
                  (!hasPrerequisitesForPublishing && !isOpen) || viewOnly
                }
              />
              <AppRadioButton
                key="closed"
                name="fundStatus"
                label="Closed"
                value={0}
                checked={field.value === 0}
                onChange={() => field.onChange(0)}
                disabled={viewOnly}
              />
              {!investment.length && (
                <AppRadioButton
                  key="draft"
                  name="fundStatus"
                  label="Draft"
                  value={2}
                  checked={isDraft}
                  onChange={() => field.onChange(2)}
                  disabled={viewOnly}
                />
              )}
            </>
          )}
        />
      </FieldRow>

      <FieldRow
        title="Show on firm link?"
        centerTitle
        tooltipText={
          <>
            If you wish to hide this tranche from your portal investment flow
            (so it is only visible for investors with this tranche's specific
            investment link), toggle this setting to{' '}
            <i>Only show on tranche link</i>.
          </>
        }
      >
        <span className={classes.switchLabel}>Only show on tranche link</span>
        <Controller
          name="trancheVisibility"
          control={control}
          render={({ field }) => (
            <Switch
              key="trancheVisibility"
              disabled={viewOnly}
              checked={field.value === TrancheVisibility.ShowOnFirm}
              onChange={(event) =>
                field.onChange(
                  event.target.checked
                    ? TrancheVisibility.ShowOnFirm
                    : TrancheVisibility.ShowOnTranche,
                )
              }
            />
          )}
        />
        <span className={classes.switchLabel}>Show on firm link</span>
      </FieldRow>

      <FieldRow
        title="Fund type*"
        centerTitle
        tooltipText={
          <div>
            <p>
              Select the type of investment(s) being made with this tranche:
            </p>
            <br />
            <ul className={classes.tooltipPadding}>
              <li>
                For mixed EIS and SEIS funds, select <b>EIS</b>.
              </li>
              <li>
                For direct investments <i>with tax relief</i> that you want to
                display individually on client portals, select <b>EIS</b> or{' '}
                <b>SEIS</b> (as applicable).
              </li>
              <li>
                For direct investments <i>with no tax relief</i> that you want
                to display individually on client portals, select <b>Direct</b>.
              </li>
              <li>
                For direct investments <i>with or without tax relief</i> you
                want to display grouped: (i) by tax year, (ii) by calendar year
                or (iii) all as one on client portals (setting adjustable in
                your{' '}
                <a
                  className={classes.tooltipLink}
                  href="/dashboard/my-firm"
                  target="_blank"
                  rel="noreferrer"
                >
                  My Firm
                </a>{' '}
                page), select <b>Syndicate</b>.
              </li>
            </ul>
          </div>
        }
        tooltipMaxWidth={600}
      >
        <Controller
          name="tag"
          control={control}
          render={({ field, fieldState }) => (
            <>
              {tagList.map((item, index) => (
                <AppRadioButton
                  key={index}
                  name="tag"
                  label={item.name}
                  value={item.id}
                  checked={field.value === item.id}
                  onChange={(event) => field.onChange(event.target.value)}
                  disabled={viewOnly}
                />
              ))}
              {fieldState.error && (
                <FormHelperText error>
                  {fieldState.error.message}
                </FormHelperText>
              )}
            </>
          )}
        />
      </FieldRow>

      <FieldRow title="Professional clients only" centerTitle>
        <Tooltip
          title={
            !allowProfessionalClients ? (
              <>
                You cannot make a tranche professional-only as your firm has
                professional investor classification switched off. To turn it
                on, change the setting in the <i>Portal and KYC</i> section of
                the <i>My Firm</i> page first.
              </>
            ) : (
              ''
            )
          }
        >
          <ToggleContainer>
            <span className={classes.switchLabel}>No</span>
            <Controller
              name="isProfessionalClientsOnly"
              control={control}
              render={({ field }) => (
                <Switch
                  disabled={!allowProfessionalClients || viewOnly}
                  key="isProfessionalClientsOnly"
                  checked={field.value}
                  onChange={(event) => field.onChange(event.target.checked)}
                />
              )}
            />
            <span className={classes.switchLabel}>Yes</span>
          </ToggleContainer>
        </Tooltip>
      </FieldRow>

      <FieldRow
        title="Fund raise limit"
        centerTitle
        tooltipText="Set an amount at which the tranche will be automatically closed."
      >
        <Controller
          name="fundRaiseLimit"
          control={control}
          render={({ field }) => (
            <NumericFormat
              name="fundRaiseLimit"
              prefix="£"
              decimalScale={2}
              fixedDecimalScale
              customInput={TextField}
              allowNegative={false}
              allowLeadingZeros={false}
              thousandSeparator
              placeholder="Enter fund raise limit"
              value={field.value ?? ''}
              onValueChange={({ value: nextValue }) =>
                field.onChange(nextValue ? Number(nextValue) : null)
              }
            />
          )}
        />
      </FieldRow>

      <FieldRow title="Start date" centerTitle>
        <Controller
          name="startDate"
          control={control}
          render={({ field }) => (
            <AppDatePicker
              name="startDate"
              value={field.value?.toUTCDate() ?? null}
              disabled={viewOnly}
              onChange={(date) =>
                field.onChange(date ? new CalendarDay(date) : null)
              }
            />
          )}
        />
      </FieldRow>

      <FieldRow title="Scheduled close date*" centerTitle>
        <Controller
          name="finishDate"
          control={control}
          render={({ field, fieldState }) => (
            <AppDatePicker
              required
              name="finishDate"
              value={field.value?.toUTCDate() ?? null}
              disabled={viewOnly}
              onChange={(date) =>
                field.onChange(date ? new CalendarDay(date) : null)
              }
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </FieldRow>
      {id && (
        <FieldRow title="Close date" centerTitle>
          <Controller
            name="closedDate"
            control={control}
            render={({ field }) => (
              <AppDatePicker
                name="closedDate"
                value={field.value?.toUTCDate() ?? null}
                onChange={(date) =>
                  field.onChange(date ? new CalendarDay(date) : null)
                }
                disabled={viewOnly}
              />
            )}
          />
        </FieldRow>
      )}

      {!viewOnly && (
        <ButtonGroup>
          <Button variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            className={classes.marginLeftBtn}
            variant="contained"
            color="primary"
            disabled={buttonDisabled}
            onClick={() => handleClick()}
          >
            {id ? 'Update' : 'Save'}
          </Button>
        </ButtonGroup>
      )}
    </RecordView>
  );
};

export default BasicInformation;
